import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { take } from 'rxjs';

import { SvcToastService } from '@solvace/component/svc-toast';
import { WorkspaceCilInspectionOpener } from '@solvace/core/features/float-add-button/service/workspace-cil-inspection-opener';
import { MainTasksService } from '@solvace/core/features/main-tasks/main-tasks.service';
import { CilAnalyticsService } from '@solvace/core/services/analytics/cil';
import { CIL_LOAD_ERROR_MESSAGE } from '@solvace/feature/modals/svc-modal-cil-execution/shared/utils/cil-load-error';
import { ChunkLoadRecoveryService, UserIndicatorsService } from 'projects/lib-shared-core/src/public-api';

import { CilCreatedInspection } from '../models/cil-inspection.types';

/**
 * Host-facing entry point of the workspace "LIL" item, shaped like the other workspace modal
 * services (`SvcModalBosService.openModalBos()`, `SvcModalDefectTagService.openModalDefectTag()`):
 * one call opens the plan picker; the picker creates the inspection and hands it to
 * `openExecution()`, which opens the execution modal and refreshes the home LIL widgets afterwards.
 */
@Injectable({ providedIn: 'root' })
export class CilNewInspectionModalService implements WorkspaceCilInspectionOpener {
  private readonly _dialog = inject(MatDialog);
  private readonly _chunkLoadRecovery = inject(ChunkLoadRecoveryService);
  private readonly _toast = inject(SvcToastService);
  private readonly _transloco = inject(TranslocoService);
  private readonly _cilAnalytics = inject(CilAnalyticsService);
  private readonly _mainTasks = inject(MainTasksService);
  private readonly _userIndicators = inject(UserIndicatorsService);

  public openNewInspection(): void {
    void this.openModalCilNewInspection();
  }

  public async openModalCilNewInspection(): Promise<void> {
    // AB#74741 AB#74910 a stale chunk after a deploy reloads the page once instead of leaving a dead click
    const chunk = await this._chunkLoadRecovery.loadLazyChunk(
      () => import('../../cil-new-inspection-modal/cil-new-inspection-modal'),
      () => this._toast.error(this._transloco.translate(CIL_LOAD_ERROR_MESSAGE))
    );
    if (!chunk) return;
    this._dialog.open(chunk.CilNewInspectionModalComponent, {
      width: '100%',
      maxWidth: '400px',
      panelClass: 'md:py-4',
      autoFocus: false,
    });
  }

  public async openExecution(created: CilCreatedInspection): Promise<void> {
    const chunk = await this._chunkLoadRecovery.loadLazyChunk(
      () => import('@solvace/feature/modals/svc-modal-cil-execution'),
      () => this._toast.error(this._transloco.translate(CIL_LOAD_ERROR_MESSAGE))
    );
    // The inspection already exists even when its modal cannot load, so the home must reflect it.
    if (!chunk) {
      this._refreshHomeLil();
      return;
    }
    this._dialog
      .open(chunk.CilExecutionModalComponent, {
        width: '100%',
        maxWidth: '700px',
        // AB#75974 full viewport height, no empty band above or below, as in the legacy
        height: '100vh',
        maxHeight: '100vh',
        panelClass: 'cil-execution-modal-panel',
        data: { inspectionUniqueId: created.inspectionUniqueId, routineUniqueId: created.routineUniqueId },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => this._refreshHomeLil());
  }

  // Same calls the home runs for its LIL card and task list (`DashboardService.getUserIndicators`,
  // `user-indicators.component.requestLilAnalytics`), so the Atividades tab shows the new inspection.
  private _refreshHomeLil(): void {
    this._userIndicators.updateLilIndicators({ loading: true });
    this._cilAnalytics
      .getInspectionIndicators()
      .pipe(take(1))
      .subscribe({
        next: (indicator) => this._userIndicators.updateLilIndicators({ amount: indicator.data.sumTotal, loading: false }),
        error: () => this._userIndicators.updateLilIndicators({ loading: false, error: true }),
      });
    this._mainTasks.getUserDrillDowns({ lil: true });
  }
}
